import { Download, Edit, Visibility } from "@mui/icons-material";
import {
  CircularProgress,
  IconButton,
  TableCell,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useInterventionsContext } from "../../../../contexts/InterventionsContext";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useOrganizationContext } from "../../../../contexts/OrganizationContext";
import { usePractitionerRoleContext } from "../../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { Base64SignPDF } from "../../../../services/dsig";
import fluxSignerService from "../../../../services/fluxsigner/FluxSignerService";
import { PostMedicationRequest } from "../../../../services/fhir";
import { PostDocumentReference } from "../../../../services/fhir/patientsummary/documentreference/PostDocumentReference";
import { PostServiceRequest } from "../../../../services/fhir/patientsummary/serviceRequest/PostServiceRequest";
import { useFhirData } from "../../../../utils/useFhirData";
import { SignatureDialog } from "./SignatureDialog";
import { buildBasedOnXmlForResources } from "../../utils/footerInterventionPersist";

export function PrescriptionActions({ row, setShowPdf, setDocToRender }: any) {
  const { state } = useLocation();
  const { appointment } = useMedicalCareContext();
  const { userPractitioner, userPractitionerRole, userSpecialties } =
    usePractitionerRoleContext();
  const { organization } = useOrganizationContext();
  const { setBase64, functions, selectedInterventions } =
    useInterventionsContext();
  const { user } = useSessionContext();
  const [loading, setLoading] = useState<boolean>(false);

  const references = state?.encounterReference;
  const [signatureDialogOpen, setSignatureDialogOpen] =
    useState<boolean>(false);

  const healthcare = useFhirData?.findParticipant(
    appointment?.participant,
    "HealthcareService"
  )?.display;
  const servicePratice = userSpecialties?.find(
    (el: any) => el.coding?.[0]?.display === healthcare
  );
  const crm = userPractitionerRole?.identifier
    ?.find((id: any) => id.system === "crm")
    ?.value?.split("/")?.[1];

  const crmValue = crm?.slice(2);
  const crmUf = crm?.slice(0, 2);
  const docReferenceData = {
    organization: {
      id: organization?.id,
      alias: organization?.alias?.[0],
      name: organization?.name,
    },
    practitioner: {
      id: userPractitioner?.id,
      name: userPractitioner?.name?.[0]?.text,
    },
    servicePratice: {
      display: servicePratice?.coding?.[0]?.display,
      code: servicePratice?.coding?.[0]?.code,
      system: "https://fluxmed.com.br/fhir/CodeSystem/CBO",
    },
    time: {
      start: appointment?.start,
      end: appointment?.end,
    },
  };

  const handlePrescription = async (base64: any, uuid: any, idValue: any) => {
    const id = idValue?.toString();
    const contained = [
      {
        resourceType: "Practitioner",
        id: "practitioner-1",
        identifier: [
          {
            ...(crmUf &&
              crmValue && {
                extension: [
                  {
                    url: "http://ehrrunner.com/fhir/StructureDefinition/BRJurisdicaoOrgaoEmissor-1.0",
                    valueCodeableConcept: {
                      coding: [
                        {
                          system:
                            "http://ehrrunner.com/fhir/CodeSystem/BRUnidadeFederacao-1.0",
                          code: crmUf,
                        },
                      ],
                    },
                  },
                ],
              }),

            type: {
              coding: [
                {
                  system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                  code: "MD",
                },
              ],
            },
            value: crmValue,
            assigner: {
              display: "CONSELHO REGIONAL DE MEDICINA (CRM)",
            },
          },
          {
            type: {
              coding: [
                {
                  system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                  code: "TAX",
                },
              ],
            },
            value: user?.username,
          },
        ],
        name: [
          {
            text: user?.name,
          },
        ],
      },
    ];

    const signatureItens = selectedInterventions?.all?.filter(
      (item: any, index: number) => row?.listIndex.includes(index)
    );

    // Documento da linha (simple-medication | simple-supplement | simple-herbalMedicine)
    // → só os itens daquele tipo entram no basedOn / POST deste PDF
    const simpleDocType =
      row?.key === "simple-medication"
        ? "medication"
        : row?.key === "simple-supplement"
          ? "supplement"
          : row?.key === "simple-herbalMedicine"
            ? "herbalMedicine"
            : undefined;

    const isSpecialTag = (tag: any) => {
      const t = tag?.toUpperCase?.() || "";
      return (
        t.includes("VERMELHA") ||
        t.includes("PRETA") ||
        t.includes("ESPECIAL")
      );
    };

    const simpleMedication = simpleDocType
      ? signatureItens?.filter(
          (el: any) =>
            el?.type === simpleDocType && !isSpecialTag(el?.tag)
        )
      : [];

    const specialMedication = signatureItens?.filter((el: any) =>
      isSpecialTag(el?.tag)
    );

    const services = signatureItens?.filter(
      (el: any) => el?.type === "service"
    );

    const procedures = signatureItens?.filter(
      (el: any) => el?.type === "procedure"
    );

    const certificates = signatureItens?.filter(
      (el: any) => el?.type === "certificate"
    );

    if (references)
      try {
        if (simpleMedication?.length) {
          const simpleResponse = await PostMedicationRequest(
            simpleMedication,
            references
          );
          // Prefere medicationRequestId (edição/PUT) — location pode faltar no PUT
          // e vários MRs do mesmo PDF compartilham o mesmo DocumentReference
          const auxSimpleBasedOn = buildBasedOnXmlForResources(
            simpleMedication,
            simpleResponse?.entry,
            "MedicationRequest"
          );

          if (auxSimpleBasedOn)
            // 1 DocumentReference com basedOn de todos os MRs do PDF (N medicamentos)
            await PostDocumentReference(
              {
                ...docReferenceData,
                base64: base64,
                codeValue: "REC_SIM",
                codeDisplay: "Receituário Simples",
                categoryValue: "prescricao",
                categoryDisplay: "Receituário",
              },
              references,
              auxSimpleBasedOn,
              true,
              uuid,
              id
            );
        }

        if (specialMedication?.length) {
          specialMedication?.map(async (special: any, index: number) => {
            const specialResponse = await PostMedicationRequest(
              [special],
              references
            );

            const auxMedicalBasedOn = buildBasedOnXmlForResources(
              [special],
              specialResponse?.entry,
              "MedicationRequest"
            );
            if (auxMedicalBasedOn) {
              await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64,
                  codeValue: "REC_ESP",
                  codeDisplay: "Receituário Especial",
                  categoryValue: "prescricao",
                  categoryDisplay: "Receituário",
                },
                references,
                auxMedicalBasedOn,
                true,
                uuid,
                id
              );
            }
            return;
          });
        }

        if (services?.length) {
          const serviceResponse = await PostServiceRequest(
            services,
            references,
            contained
          );

          const auxServiceBasedOn = buildBasedOnXmlForResources(
            services,
            serviceResponse?.entry,
            "ServiceRequest"
          );

          if (auxServiceBasedOn) {
            await PostDocumentReference(
              {
                ...docReferenceData,
                base64: base64,
                codeValue: "SERVICE_REQUEST",
                codeDisplay: "Solicitação de exame",
                categoryValue: "service-request",
                categoryDisplay: "Solicitação",
              },
              references,
              auxServiceBasedOn,
              true,
              uuid,
              id
            );
          }
        }

        if (procedures?.length) {
          const procedureResponse = await PostServiceRequest(
            procedures,
            references,
            contained
          );

          const auxProcedureBasedOn = buildBasedOnXmlForResources(
            procedures,
            procedureResponse?.entry,
            "ServiceRequest"
          );

          if (auxProcedureBasedOn) {
            await PostDocumentReference(
              {
                ...docReferenceData,
                base64,
                codeValue: "SERVICE_REQUEST",
                codeDisplay: "Procedimento",
                categoryValue: "service-request",
                categoryDisplay: "Procedimento",
              },
              references,
              auxProcedureBasedOn,
              true,
              uuid,
              id
            );
          }
        }
        if (certificates?.length) {
          certificates?.map(async (certificates: any, index: number) => {
            return await PostDocumentReference(
              {
                ...docReferenceData,
                base64,
                codeValue: "AT",
                codeDisplay: "Atestado",
                categoryValue: "atestado",
                categoryDisplay: "Atestado Médico",
              },
              references,
              null,
              true,
              uuid,
              id
            );
          });
        }
        toast.success("Prescrição realizada com sucesso!");
      } catch (err) {
        console.log("err", err);
      }
  };

  const handleSignature = async (
    provider: "vidaas" | "safeid" | "fluxsigner",
    password?: string,
    lifeTime?: number,
    certificateAlias?: string
  ) => {
    setLoading(true);
    toast.warn("Aguardando verificação e segurança do seu provedor!");
    try {
      let response;
      
      if (provider === "fluxsigner") {
        // Assinatura digital com FluxSigner
        if (!certificateAlias) {
          toast.error("Nenhum certificado selecionado.");
          setLoading(false);
          return;
        }

        // Usar o base64 diretamente
        const pdfBase64 = row?.base64?.split(",")?.[1];
        
        if (!pdfBase64) {
          toast.error("PDF não encontrado.");
          setLoading(false);
          return;
        }

        const signResponse = await fluxSignerService.signPDFBase64(pdfBase64, certificateAlias, {
          reason: "Prescrição médica via FluxMed",
          location: "Brasil",
          useTimestamp: false,
        });
        response = signResponse.signedPdfData;

      } else {
        // Assinatura com VidaaS ou SafeID
        response = await Base64SignPDF({
          provider: provider,
          ...(provider === "safeid" && {
            userPassword: password,
          }),
          pdfBase64: row?.base64?.split(",")?.[1],
          cpfCnpj: user?.username,
          lifeTime: lifeTime ?? 0,
        });
      }

      if (!response?.length) throw new Error("Falha na assinatura");

      await handlePrescription(
        response?.replace(/\r\n/g, ""),
        row?.uuid,
        row?.idValue
      );
      row?.listIndex?.map((el: any) =>
        functions.handleSign(el, response?.replace(/\r\n/g, ""))
      );

      setBase64((prevItems: any) => {
        const newData = [...prevItems];
        if (newData[row?.index][row?.key]) {
          newData[row?.index][row?.key]["sign"] = true;
        }
        return newData;
      });

      toast.success("Assinatura realizada!");
    } catch (error) {
      toast.warn(
        "Não foi possível realizar a assinatura, por favor tente novamente!"
      );
      console.error("HandleSignature error:", error);
    } finally {
      setLoading(false);
    }
  };

  function downloadBase64File(base64: string, type: string) {
    const linkSource = base64;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = type;
    downloadLink.click();
  }

  function handleShowPDF() {
    setDocToRender(row?.base64);
    setShowPdf(true);
  }

  return (
    <>
      <TableCell align="center" sx={{ minWidth: "145px" }}>
        <Tooltip title="Visualizar">
          <IconButton onClick={() => handleShowPDF()}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Baixar">
          <IconButton
            onClick={() => downloadBase64File(row?.base64, row?.type)}
          >
            <Download fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            row?.sign === "Pendente"
              ? "Realizar assinatura"
              : "Assinatura concluída"
          }
        >
          <IconButton
            onClick={() => {
              if (row?.sign === "Pendente") {
                setSignatureDialogOpen(true);
              }
            }}
          >
            {loading ? <CircularProgress size={20} /> : <Edit />}
          </IconButton>
        </Tooltip>
        {signatureDialogOpen && (
          <SignatureDialog
            visualize={row?.base64?.split(",")?.[1]}
            isOpen={signatureDialogOpen}
            handleClose={() => setSignatureDialogOpen(false)}
            handleSignature={handleSignature}
            row={row}
            loading={loading}
          />
        )}
      </TableCell>
    </>
  );
}
